import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  createLiveSession,
  getLiveSession,
  updateLiveSession,
  endLiveSession,
  getLiveSessionsForPatient,
  getActiveLiveSession,
  getActiveLiveSessionsForTherapist,
} from "../../firebase";

export const liveSessionsApi = createApi({
  reducerPath: "liveSessionsApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["LiveSessions", "LiveSession", "ActiveLiveSession"],
  endpoints: (builder) => ({
    // Get live sessions for a patient (for import selection)
    getLiveSessionsForPatient: builder.query({
      async queryFn({ clinicId, patientId, therapistId }) {
        try {
          const sessions = await getLiveSessionsForPatient(
            clinicId,
            patientId,
            therapistId
          );
          return { data: sessions };
        } catch (error) {
          console.error("[CK-9100] Error fetching live sessions for patient:", {
            errorCode: "CK-9100",
            errorMessage: error.message,
            clinicId,
            patientId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { patientId }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "LiveSessions", id })),
              { type: "LiveSessions", id: `PATIENT_${patientId}` },
            ]
          : [{ type: "LiveSessions", id: `PATIENT_${patientId}` }],
    }),

    // Get a single live session
    getLiveSession: builder.query({
      async queryFn({ clinicId, sessionId }) {
        try {
          const session = await getLiveSession(clinicId, sessionId);
          if (!session) {
            return { error: { message: "Live session not found" } };
          }
          return { data: session };
        } catch (error) {
          console.error("[CK-9101] Error fetching live session:", {
            errorCode: "CK-9101",
            errorMessage: error.message,
            clinicId,
            sessionId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { sessionId }) => [
        { type: "LiveSession", id: sessionId },
      ],
    }),

    // Get active live session for therapist (if any)
    getActiveLiveSession: builder.query({
      async queryFn({ clinicId, therapistId, appointmentId }) {
        try {
          const session = await getActiveLiveSession(
            clinicId,
            therapistId,
            appointmentId
          );
          return { data: session };
        } catch (error) {
          console.error("[CK-9102] Error fetching active live session:", {
            errorCode: "CK-9102",
            errorMessage: error.message,
            clinicId,
            therapistId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { therapistId }) => [
        { type: "ActiveLiveSession", id: therapistId },
      ],
    }),

    // Get all active live sessions for therapist (for carousel - sessions where appointment ended)
    getActiveLiveSessionsForTherapist: builder.query({
      async queryFn({ clinicId, therapistId }) {
        try {
          const sessions = await getActiveLiveSessionsForTherapist(
            clinicId,
            therapistId
          );
          return { data: sessions };
        } catch (error) {
          console.error("[CK-9106] Error fetching active live sessions for therapist:", {
            errorCode: "CK-9106",
            errorMessage: error.message,
            clinicId,
            therapistId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { therapistId }) => [
        { type: "ActiveLiveSession", id: therapistId },
        { type: "ActiveLiveSession", id: "LIST" },
      ],
    }),

    // Create a new live session
    createLiveSession: builder.mutation({
      async queryFn({ clinicId, sessionData, therapistId, therapistName }) {
        try {
          const session = await createLiveSession(
            clinicId,
            sessionData,
            therapistId,
            therapistName
          );
          return { data: session };
        } catch (error) {
          console.error("[CK-9103] Error creating live session:", {
            errorCode: "CK-9103",
            errorMessage: error.message,
            clinicId,
            therapistId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { sessionData, therapistId }) => [
        { type: "LiveSessions", id: `PATIENT_${sessionData.patientId}` },
        { type: "ActiveLiveSession", id: therapistId },
      ],
    }),

    // Update a live session (auto-save)
    updateLiveSession: builder.mutation({
      async queryFn({ clinicId, sessionId, sessionData, therapistId }) {
        try {
          const session = await updateLiveSession(
            clinicId,
            sessionId,
            sessionData,
            therapistId
          );
          return { data: session };
        } catch (error) {
          console.error("[CK-9104] Error updating live session:", {
            errorCode: "CK-9104",
            errorMessage: error.message,
            clinicId,
            sessionId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { sessionId, therapistId }) => [
        { type: "LiveSession", id: sessionId },
        { type: "ActiveLiveSession", id: therapistId },
      ],
    }),

    // End a live session
    endLiveSession: builder.mutation({
      async queryFn({ clinicId, sessionId, sessionData, therapistId, therapistName }) {
        try {
          const session = await endLiveSession(
            clinicId,
            sessionId,
            sessionData,
            therapistId,
            therapistName
          );
          return { data: session };
        } catch (error) {
          console.error("[CK-9105] Error ending live session:", {
            errorCode: "CK-9105",
            errorMessage: error.message,
            clinicId,
            sessionId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { sessionData, therapistId }) => [
        { type: "LiveSessions", id: `PATIENT_${sessionData?.patientId}` },
        { type: "ActiveLiveSession", id: therapistId },
        { type: "ActiveLiveSession", id: "LIST" },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch({
            type: 'calendarApi/invalidateTags',
            payload: [
              { type: 'Appointments', id: 'LIST' },
              { type: 'Appointments', id: 'PENDING_ACTION' },
              { type: 'Finance', id: 'STATS' },
              { type: 'Finance', id: 'UPCOMING' },
              { type: 'Finance', id: 'PENDING_PAYMENTS' },
            ],
          });
          dispatch({
            type: 'financeApi/invalidateTags',
            payload: [
              { type: "TransactionStats", id: "MONTHLY" },
              { type: "Transactions", id: "EXPECTED" },
              { type: "KanbanTransactions", id: "LIST" },
              { type: "PendingCollection", id: "LIST" },
            ],
          });
        } catch { /* mutation failed */ }
      },
    }),
  }),
});

export const {
  useGetLiveSessionsForPatientQuery,
  useGetLiveSessionQuery,
  useGetActiveLiveSessionQuery,
  useGetActiveLiveSessionsForTherapistQuery,
  useCreateLiveSessionMutation,
  useUpdateLiveSessionMutation,
  useEndLiveSessionMutation,
} = liveSessionsApi;
